import { Dialog, Transition } from '@headlessui/react';
import { Fragment, PropsWithChildren } from 'react';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  showCloseButton?: boolean;
}

const Modal = ({
  open,
  onClose,
  title,
  description,
  children,
  showCloseButton = false,
}: PropsWithChildren<ModalProps>) => {
  return (
    <Transition show={open} as={Fragment} appear>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-surface-default/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-0 text-center md:items-center md:p-8">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-10 md:translate-y-0 md:scale-95"
              enterTo="opacity-100 translate-y-0 md:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 md:scale-100"
              leaveTo="opacity-0 translate-y-10 md:translate-y-0 md:scale-95"
            >
              <Dialog.Panel className="relative w-full transform rounded-t-3xl bg-surface-secondary p-6 text-left align-middle shadow-xl transition md:max-w-2xl md:rounded-2xl md:p-8">
                {showCloseButton ? (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close modal"
                    className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-default bg-surface-default/30 text-sm font-semibold text-text-muted transition hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-default focus-visible:ring-offset-2 focus-visible:ring-offset-surface-secondary md:right-6 md:top-6"
                  >
                    X
                  </button>
                ) : null}
                {title ? (
                  <Dialog.Title as="h3" className="text-lg font-semibold text-text-primary">
                    {title}
                  </Dialog.Title>
                ) : null}
                {description ? (
                  <Dialog.Description className="mt-1 text-sm text-text-muted">
                    {description}
                  </Dialog.Description>
                ) : null}
                <div className="mt-4 space-y-4 text-sm text-text-primary">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
